/**
 * Contenteditable input handler, selection tracking, keyboard handler, icon tinting.
 * Verbatim slice of the iframe bridge script (optimization Phase 10c) —
 * returned text is embedded inside the bridge IIFE; it is NOT module code.
 * The slice is interpolation-free: keep it that way (head/tail interpolations
 * live in iframe-bridge.js).
 */

export function editingInputLayer() {
  return String.raw`  // --- Input handler for contenteditable ---
  var PROTECTED_EDIT_CHILD_SELECTOR = 'img,picture,svg,canvas,video,audio,input,textarea,select,option,optgroup,button,fieldset,legend,output,progress,meter,datalist,.cs-icon,.cs-icon-wrapper,[aria-hidden="true"],[class*="icon"]';

  function _outerProtectedRoot(node) {
    var el = node && node.nodeType === 1 ? node : (node && node.parentElement);
    if (!el) return null;
    var match = el.matches && el.matches(PROTECTED_EDIT_CHILD_SELECTOR)
      ? el
      : (el.closest ? el.closest(PROTECTED_EDIT_CHILD_SELECTOR) : null);
    if (!match || !activeEditable.contains(match)) {
      match = el.querySelector ? el.querySelector(PROTECTED_EDIT_CHILD_SELECTOR) : null;
    }
    if (!match || !activeEditable.contains(match)) return null;
    var parentMatch = match.parentElement && match.parentElement.closest(PROTECTED_EDIT_CHILD_SELECTOR);
    while (parentMatch && activeEditable.contains(parentMatch)) {
      match = parentMatch;
      parentMatch = match.parentElement && match.parentElement.closest(PROTECTED_EDIT_CHILD_SELECTOR);
    }
    return match;
  }

  function _trackProtectedDeletion(node) {
    var protectedNode = _outerProtectedRoot(node);
    if (!protectedNode) return;
    var path = _getNodePath(activeEditable, protectedNode);
    if (!path) return;
    var signature = path.join('.');
    var alreadyTracked = _intentionallyRemovedProtectedPaths.some(function(existing) {
      return existing.join('.') === signature;
    });
    if (!alreadyTracked) _intentionallyRemovedProtectedPaths.push(path);
  }

  function _adjacentNodeAtCaret(range, backwards) {
    var current = range.startContainer;
    var offset = range.startOffset;
    if (current.nodeType === 1) {
      return current.childNodes[offset + (backwards ? -1 : 0)] || null;
    }
    if (current.nodeType === 3) {
      var textLength = (current.nodeValue || '').length;
      if ((backwards && offset > 0) || (!backwards && offset < textLength)) return null;
    }
    while (current && current !== activeEditable) {
      var sibling = backwards ? current.previousSibling : current.nextSibling;
      if (sibling) return sibling;
      current = current.parentNode;
    }
    return null;
  }

  document.addEventListener('beforeinput', function(e) {
    if (
      _plainTextOnly &&
      activeEditable &&
      (String(e.inputType || '').indexOf('format') === 0 ||
        e.inputType === 'insertLink' ||
        e.inputType === 'insertOrderedList' ||
        e.inputType === 'insertUnorderedList')
    ) {
      e.preventDefault();
      return;
    }
    if (!activeEditable || !String(e.inputType || '').startsWith('delete')) return;
    if (!(activeEditable.contains(e.target) || e.target === activeEditable)) return;
    var range = _getSelectionRangeWithinActiveEditable();
    if (!range) return;
    if (range.collapsed) {
      var backwards = String(e.inputType || '').toLowerCase().indexOf('backward') !== -1;
      _trackProtectedDeletion(_adjacentNodeAtCaret(range, backwards));
      return;
    }
    var protectedNodes = activeEditable.querySelectorAll(PROTECTED_EDIT_CHILD_SELECTOR);
    for (var i = 0; i < protectedNodes.length; i++) {
      var protectedNode = protectedNodes[i];
      var nestedProtectedParent = protectedNode.parentElement && protectedNode.parentElement.closest(PROTECTED_EDIT_CHILD_SELECTOR);
      if (nestedProtectedParent && activeEditable.contains(nestedProtectedParent)) continue;
      try {
        if (!range.intersectsNode(protectedNode)) continue;
      } catch (err) {
        continue;
      }
      _trackProtectedDeletion(protectedNode);
    }
  });

  document.addEventListener('input', function(e) {
    if (!activeEditable) return;
    if (activeEditable.contains(e.target) || e.target === activeEditable) {
      _lastPastePayload = null;
      _lastPasteRangeSnapshot = null;
      _lastPasteInsertedNodes = null;
      _lastPasteReplacedNodes = null;
      _lastUndonePastePayload = null;
      _editOperationRedoStack = [];
      onContentChanged();
    }
  });

  document.addEventListener('compositionstart', function(e) {
    if (!activeEditable || !(activeEditable.contains(e.target) || e.target === activeEditable)) return;
    _isComposingText = true;
    clearTimeout(debounceTimer);
    debounceTimer = null;
  });

  document.addEventListener('compositionend', function(e) {
    if (!activeEditable || !(activeEditable.contains(e.target) || e.target === activeEditable)) return;
    _isComposingText = false;
    onContentChanged();
  });

  document.addEventListener('focusout', function(e) {
    if (!activeEditable || !(activeEditable.contains(e.target) || e.target === activeEditable)) return;
    setTimeout(function() {
      if (!activeEditable || !document.hasFocus()) return;
      var next = document.activeElement;
      if (!next || next === document.body || activeEditable.contains(next)) return;
      finishEditing();
    }, 0);
  });

  var _selectionSyncRafId = null;
  var _sentSelectionState = 'none';
  var _lastBoundInfoSignature = null;

  function _scheduleSelectionSync() {
    if (!activeEditable) return;
    if (_selectionSyncRafId) return;
    _selectionSyncRafId = requestAnimationFrame(function() {
      _selectionSyncRafId = null;
      _emitSelectionState();
    });
  }

  function _sendSelectionCleared() {
    if (_sentSelectionState === 'cleared') return;
    _sentSelectionState = 'cleared';
    send('selection-cleared', null);
    _emitBoundInfo(null);
  }

  function _bindingLabelForKey(bindingKey) {
    if (!bindingKey) return '';
    return CLIENT_TEXT_LABELS[bindingKey] || CLIENT_LINK_LABELS[bindingKey] || bindingKey;
  }

  function _parseClientLinkKey(hrefValue) {
    var href = String(hrefValue || '').trim();
    if (!href || href.indexOf(CLIENT_LINK_MARKER_PREFIX) !== 0) return '';
    var key = href.slice(CLIENT_LINK_MARKER_PREFIX.length).trim();
    return key || '';
  }

  function _closestElementWithin(node, root, selector) {
    if (!node || !root) return null;
    var current = node.nodeType === 1 ? node : node.parentElement;
    while (current && current !== root) {
      if (current.matches && current.matches(selector)) return current;
      current = current.parentElement;
    }
    if (root.matches && root.matches(selector)) return root;
    return null;
  }

  function _escapeSelectorValue(value) {
    if (window.CSS && typeof window.CSS.escape === 'function') {
      return window.CSS.escape(String(value || ''));
    }
    return String(value || '').replace(/["\\]/g, '\\$&');
  }

  function _sanitizeFontSlug(value) {
    var raw = String(value || '').trim().toLowerCase();
    var safe = raw.replace(/[^a-z0-9-]/g, '');
    return safe === raw ? safe : '';
  }

  function _isAllowedFontSlug(slug) {
    if (!slug) return true;
    if (!__PB_ALLOWED_FONT_SLUGS__ || !__PB_ALLOWED_FONT_SLUGS__.length) return false;
    return __PB_ALLOWED_FONT_SLUGS__.indexOf(slug) !== -1;
  }

  function _fontMarkerFromName(value) {
    return String(value || '').trim().toLowerCase().replace(/['"]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  }

  function _fontLinkAlreadyExists(href, marker) {
    if (marker && document.head.querySelector('link[data-pb-inline-font="' + _escapeSelectorValue(marker) + '"]')) {
      return true;
    }
    if (marker) return false;
    var links = document.head.querySelectorAll ? document.head.querySelectorAll('link[rel="stylesheet"]') : [];
    for (var i = 0; i < links.length; i++) {
      if (links[i].getAttribute('href') === href || links[i].href === href) return true;
    }
    return false;
  }

  function _syncInlineFontRuntimeCSS() {
    var style = document.getElementById('pb-inline-font-rules');
    if (!style) {
      style = document.createElement('style');
      style.id = 'pb-inline-font-rules';
      document.head.appendChild(style);
    }
    var used = {};
    var nodes = document.querySelectorAll('[class*="pb-font-"]');
    for (var i = 0; i < nodes.length; i++) {
      for (var j = 0; j < nodes[i].classList.length; j++) {
        var cls = nodes[i].classList[j];
        if (cls.indexOf('pb-font-') !== 0) continue;
        var slug = _sanitizeFontSlug(cls.slice('pb-font-'.length));
        if (_isAllowedFontSlug(slug) && __PB_INLINE_FONT_RULES__[slug]) used[slug] = true;
      }
    }
    var rules = ['/* pb-font-runtime-v1 */'];
    for (var k = 0; k < __PB_ALLOWED_FONT_SLUGS__.length; k++) {
      var allowedSlug = __PB_ALLOWED_FONT_SLUGS__[k];
      if (used[allowedSlug] && __PB_INLINE_FONT_RULES__[allowedSlug]) {
        rules.push(__PB_INLINE_FONT_RULES__[allowedSlug]);
        _injectFontLoader(__PB_INLINE_FONT_LOADERS__[allowedSlug], allowedSlug);
      }
    }
    style.textContent = rules.join('\n');
    _pruneUnusedInlineFontLoaders();
  }

  function _pruneUnusedInlineFontLoaders() {
    var links = document.head.querySelectorAll('link[data-pb-inline-font]');
    for (var i = 0; i < links.length; i++) {
      var slug = _sanitizeFontSlug(links[i].getAttribute('data-pb-inline-font') || '');
      if (!slug || !document.querySelector('.pb-font-' + _escapeSelectorValue(slug))) {
        links[i].remove();
      }
    }
  }

  function _injectFontLoader(loader, marker) {
    if (!loader || !loader.href || typeof loader.href !== 'string') return;
    if (loader.href.indexOf('https://') !== 0 && loader.href.indexOf('http://') !== 0) return;
    var safeMarker = _sanitizeFontSlug(marker || '');
    if (_fontLinkAlreadyExists(loader.href, safeMarker)) return;
    var link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = loader.href;
    if (safeMarker) link.setAttribute('data-pb-inline-font', safeMarker);
    document.head.appendChild(link);
  }

  function _setGlobalFontLoader(role, loader, marker) {
    var safeRole = role === 'heading' ? 'heading' : 'body';
    var id = 'pb-' + safeRole + '-font-loader';
    var existing = document.getElementById(id);
    if (!loader || !loader.href || typeof loader.href !== 'string'
      || (loader.href.indexOf('https://') !== 0 && loader.href.indexOf('http://') !== 0)) {
      if (existing) existing.remove();
      return;
    }
    if (existing && existing.getAttribute('href') === loader.href) return;
    var link = existing || document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = loader.href;
    link.setAttribute('data-pb-font-role', safeRole);
    link.setAttribute('data-pb-font-family', _sanitizeFontSlug(marker || ''));
    if (!existing) document.head.appendChild(link);
  }

  function _sectionWrapperByInstanceId(instanceId) {
    if (!instanceId) return null;
    return document.querySelector('[data-pb-instance="' + _escapeSelectorValue(instanceId) + '"]');
  }

  function _setSectionAttr(el, name, enabled) {
    if (!el || !el.setAttribute || !el.removeAttribute) return;
    if (enabled) el.setAttribute(name, 'true');
    else el.removeAttribute(name);
  }

  function _clearSectionChromeState(el) {
    if (!el || !el.removeAttribute) return;
    el.removeAttribute('data-pb-section-hovered');
    el.removeAttribute('data-pb-section-selected');
    el.removeAttribute('data-pb-section-element-hovered');
    el.removeAttribute('data-pb-section-first');
    el.removeAttribute('data-pb-section-last');
    el.removeAttribute('data-pb-section-multi');
    el.removeAttribute('data-pb-multi-count');
    el.removeAttribute('data-pb-section-changed');
    el.removeAttribute('data-pb-change-label');
    el.removeAttribute('data-pb-ai-context');
    el.removeAttribute('data-pb-ai-context-marker');
    var contextMarkers = el.querySelectorAll
      ? el.querySelectorAll('[data-pb-ai-context-marker]')
      : [];
    for (var cm = 0; cm < contextMarkers.length; cm++) {
      if (contextMarkers[cm].parentNode) {
        contextMarkers[cm].parentNode.removeChild(contextMarkers[cm]);
      }
    }
    el.classList.remove('pb-section-flash');
  }

  function _syncSectionEdgeState() {
    var wrappers = Array.prototype.slice.call(document.querySelectorAll('[data-pb-instance]'));
    for (var i = 0; i < wrappers.length; i++) {
      _setSectionAttr(wrappers[i], 'data-pb-section-first', i === 0);
      _setSectionAttr(wrappers[i], 'data-pb-section-last', i === wrappers.length - 1);
    }
    if (_selectedSectionInstanceId && !_sectionWrapperByInstanceId(_selectedSectionInstanceId)) {
      _selectedSectionInstanceId = null;
    }
    if (_hoveredSectionInstanceId && !_sectionWrapperByInstanceId(_hoveredSectionInstanceId)) {
      _hoveredSectionInstanceId = null;
    }
    // 16A1 — same stale-id prune the scalar gets: a co-selected wrapper that
    // vanished (peer delete, undo) drops out; the survivors keep their attrs.
    if (_multiSelectedInstanceIds.length) {
      var surviving = [];
      for (var m = 0; m < _multiSelectedInstanceIds.length; m++) {
        if (_sectionWrapperByInstanceId(_multiSelectedInstanceIds[m])) {
          surviving.push(_multiSelectedInstanceIds[m]);
        }
      }
      if (surviving.length !== _multiSelectedInstanceIds.length) {
        _setMultiSelectedSections(surviving, _selectedSectionInstanceId);
      }
    }
  }

  // 16A1 — stamp the co-selected wrappers. data-pb-section-multi marks every
  // member EXCEPT the lead (whose scalar attr already paints it); the lead
  // carries data-pb-multi-count so its label pill gains a "+N more" suffix via
  // the existing attr() mechanism — one pill, not N stacked ones.
  function _setMultiSelectedSections(instanceIds, leadId) {
    var stale = document.querySelectorAll('[data-pb-section-multi], [data-pb-multi-count]');
    for (var i = 0; i < stale.length; i++) {
      stale[i].removeAttribute('data-pb-section-multi');
      stale[i].removeAttribute('data-pb-multi-count');
    }
    _multiSelectedInstanceIds = [];
    var ids = instanceIds || [];
    if (ids.length < 2) return;
    _multiSelectedInstanceIds = ids.slice();
    for (var j = 0; j < ids.length; j++) {
      if (ids[j] === leadId) continue;
      _setSectionAttr(_sectionWrapperByInstanceId(ids[j]), 'data-pb-section-multi', true);
    }
    var leadWrapper = _sectionWrapperByInstanceId(leadId);
    if (leadWrapper) {
      leadWrapper.setAttribute('data-pb-multi-count', '+' + (ids.length - 1) + ' more');
    }
  }

  // 16C1 — stamp the co-selected repeat-root siblings. data-pb-element-multi
  // marks every member EXCEPT the lead (whose data-pb-selected-target already
  // paints it), so a 5-icon selection reads as one set with one label pill
  // rather than five competing ones. Below size 2 this is a pure clear.
  function _setMultiSelectedElements(eids, leadEid, itemSelector) {
    var stale = document.querySelectorAll('[data-pb-element-multi]');
    for (var i = 0; i < stale.length; i++) {
      stale[i].removeAttribute('data-pb-element-multi');
    }
    var ids = eids || [];
    if (ids.length < 2) return;
    for (var j = 0; j < ids.length; j++) {
      if (!ids[j] || ids[j] === leadEid) continue;
      var el = document.querySelector('[data-pb-eid="' + _escapeSelectorValue(ids[j]) + '"]');
      if (!el) continue;
      // A unit identity eid can sit on a DESCENDANT of the repeat root (eids
      // are stamped on editables only), so climb to the unit before painting —
      // otherwise the outline lands on an icon inside the card instead of the
      // card. Without a selector the eid itself is the best available box.
      var unit = itemSelector && el.closest ? el.closest(itemSelector) || el : el;
      unit.setAttribute('data-pb-element-multi', 'true');
    }
  }

  function _flashSectionWrapper(wrapper) {
    if (!wrapper || !wrapper.classList) return;
    if (wrapper._pbSectionFlashTimer) {
      clearTimeout(wrapper._pbSectionFlashTimer);
      wrapper._pbSectionFlashTimer = null;
    }
    wrapper.classList.remove('pb-section-flash');
    void wrapper.offsetWidth;
    wrapper.classList.add('pb-section-flash');
    wrapper._pbSectionFlashTimer = setTimeout(function() {
      wrapper.classList.remove('pb-section-flash');
      wrapper._pbSectionFlashTimer = null;
    }, 800);
  }

  // Find & Replace navigate-to-hit: locate a (beforeCtx + match + afterCtx)
  // text run inside the host element, wrap the match slice in a transient
  // span with class pb-find-match-focus, return the span so the caller can
  // unwrap it after the pulse. Returns null if not found.
  //
  // Match algorithm: walk text nodes in DOM order. For each, look for the
  // FULL signature (beforeCtx + match + afterCtx) first — that is the most
  // selective match and dedupes duplicates like "Acme ... Acme" in one node.
  // Fall back to a context-relaxed search (just the match string) if the
  // surrounding text drifted (e.g. iframe re-render normalized whitespace).
  function _findAndWrapMatch(host, beforeCtx, match, afterCtx) {
    if (!host || !match) return null;
    var doc = host.ownerDocument || document;
    var walker = doc.createTreeWalker(host, 4 /* SHOW_TEXT */);
    var nodes = [];
    var n;
    while ((n = walker.nextNode())) nodes.push(n);
    var signature = String(beforeCtx || '') + match + String(afterCtx || '');
    // Pass 1: full signature.
    for (var i = 0; i < nodes.length; i++) {
      var text = nodes[i].nodeValue || '';
      var sigIdx = text.indexOf(signature);
      if (sigIdx !== -1) {
        return _wrapTextRange(nodes[i], sigIdx + String(beforeCtx || '').length, match.length);
      }
    }
    // Pass 2: just the match, no context. Less selective but rescues drift.
    for (var j = 0; j < nodes.length; j++) {
      var text2 = nodes[j].nodeValue || '';
      var mIdx = text2.indexOf(match);
      if (mIdx !== -1) {
        return _wrapTextRange(nodes[j], mIdx, match.length);
      }
    }
    return null;
  }

  function _wrapTextRange(textNode, start, length) {
    if (!textNode || textNode.nodeType !== 3) return null;
    var text = textNode.nodeValue || '';
    if (start < 0 || start + length > text.length) return null;
    var doc = textNode.ownerDocument || document;
    var before = text.slice(0, start);
    var matchText = text.slice(start, start + length);
    var after = text.slice(start + length);
    var span = doc.createElement('span');
    span.className = 'pb-find-match-focus';
    span.textContent = matchText;
    var frag = doc.createDocumentFragment();
    if (before) frag.appendChild(doc.createTextNode(before));
    frag.appendChild(span);
    if (after) frag.appendChild(doc.createTextNode(after));
    textNode.parentNode.replaceChild(frag, textNode);
    return span;
  }

  function _unwrapMatchFocus(span) {
    if (!span || !span.parentNode) return;
    var parent = span.parentNode;
    // Move children out of the span (there's only a text node) and remove it.
    while (span.firstChild) parent.insertBefore(span.firstChild, span);
    parent.removeChild(span);
    if (parent.normalize) parent.normalize();
  }

  function _setHoveredSection(instanceId, options) {
    var nextId = instanceId || null;
    var elementHovered = !!(options && options.elementHovered);
    if (_hoveredSectionInstanceId && _hoveredSectionInstanceId !== nextId) {
      var previous = _sectionWrapperByInstanceId(_hoveredSectionInstanceId);
      _setSectionAttr(previous, 'data-pb-section-hovered', false);
      _setSectionAttr(previous, 'data-pb-section-element-hovered', false);
    }
    _hoveredSectionInstanceId = nextId;
    if (!nextId) return;
    var wrapper = _sectionWrapperByInstanceId(nextId);
    _setSectionAttr(wrapper, 'data-pb-section-hovered', true);
    _setSectionAttr(wrapper, 'data-pb-section-element-hovered', elementHovered);
  }

  function _setSelectedSection(instanceId, options) {
    var nextId = instanceId || null;
    if (_selectedSectionInstanceId && _selectedSectionInstanceId !== nextId) {
      var previous = _sectionWrapperByInstanceId(_selectedSectionInstanceId);
      _setSectionAttr(previous, 'data-pb-section-selected', false);
      if (previous) previous.classList.remove('pb-section-flash');
    }
    _selectedSectionInstanceId = nextId;
    if (!nextId) return;
    var wrapper = _sectionWrapperByInstanceId(nextId);
    _setSectionAttr(wrapper, 'data-pb-section-selected', true);
    if (options && options.flash) _flashSectionWrapper(wrapper);
  }

  function _clearSelectedSection(reason) {
    if (_selectedSectionInstanceId) {
      var selected = _sectionWrapperByInstanceId(_selectedSectionInstanceId);
      _setSectionAttr(selected, 'data-pb-section-selected', false);
      if (selected) selected.classList.remove('pb-section-flash');
    }
    _selectedSectionInstanceId = null;
    // A clear clears the whole selection — stale data-pb-section-multi /
    // data-pb-multi-count attrs must not outlive the scalar (16A1).
    _setMultiSelectedSections([], null);
  }

  function _describeBoundContextForSelection() {
    if (!activeEditable) return null;
    var eid = activeEditable.getAttribute('data-pb-eid') || null;
    var payload = {
      eid: eid,
      editSessionId: _activeEditSessionId
    };

    var sel = window.getSelection();
    if (!sel || !sel.rangeCount) return payload;
    var range = sel.getRangeAt(0);
    if (!activeEditable.contains(range.commonAncestorContainer)) return payload;

    var anchorTextBound = _closestElementWithin(range.startContainer, activeEditable, 'a[data-pb-client-text]');
    if (anchorTextBound) {
      var textKey = anchorTextBound.getAttribute('data-pb-client-text') || '';
      if (textKey) {
        payload.bindingKind = 'text';
        payload.bindingKey = textKey;
        payload.label = _bindingLabelForKey(textKey);
        return payload;
      }
    }

    var textBound = _closestElementWithin(range.startContainer, activeEditable, '[data-pb-client]');
    if (textBound) {
      var spanKey = textBound.getAttribute('data-pb-client') || '';
      if (spanKey) {
        payload.bindingKind = 'text';
        payload.bindingKey = spanKey;
        payload.label = _bindingLabelForKey(spanKey);
        return payload;
      }
    }

    var hrefBoundAnchor = _closestElementWithin(range.startContainer, activeEditable, 'a[href]');
    if (hrefBoundAnchor) {
      var hrefKey = _parseClientLinkKey(hrefBoundAnchor.getAttribute('href') || '');
      if (hrefKey) {
        payload.bindingKind = 'href';
        payload.bindingKey = hrefKey;
        payload.label = _bindingLabelForKey(hrefKey);
      }
    }

    return payload;
  }

  function _emitBoundInfo(info) {
    var signature = 'none';
    if (info) {
      signature = [
        info.eid || '',
        info.editSessionId || 0,
        info.bindingKind || '',
        info.bindingKey || ''
      ].join('|');
    }
    if (_lastBoundInfoSignature === signature) return;
    _lastBoundInfoSignature = signature;
    send('bound-element-info', info);
  }

  function _syncBoundInfoFromSelection() {
    if (!activeEditable) {
      _emitBoundInfo(null);
      return;
    }
    _emitBoundInfo(_describeBoundContextForSelection());
  }

  function _sendBoundInfoFromActiveEditable() {
    if (!activeEditable) {
      _emitBoundInfo(null);
      return;
    }
    _emitBoundInfo({
      eid: activeEditable.getAttribute('data-pb-eid') || null,
      editSessionId: _activeEditSessionId
    });
  }

  function _emitSelectionState() {
    if (!activeEditable) {
      _sendSelectionCleared();
      return;
    }
    var sel = window.getSelection();
    if (!sel || !sel.rangeCount) {
      _sendSelectionCleared();
      return;
    }
    var range = sel.getRangeAt(0);
    if (!activeEditable.contains(range.commonAncestorContainer)) {
      _sendSelectionCleared();
      return;
    }

    var fe = window.__pbFormatEngine;
    var inspectionRange = range;
    if (range.collapsed && fe && typeof fe.expandRangeToWord === 'function') {
      inspectionRange = fe.expandRangeToWord(range, activeEditable) || range;
    }
    lastRange = range.cloneRange();
    var rect = typeof inspectionRange.getBoundingClientRect === 'function'
      ? inspectionRange.getBoundingClientRect()
      : activeEditable.getBoundingClientRect();
    var boldActive = fe ? fe.isFormatActive('strong', inspectionRange) : document.queryCommandState('bold');
    var italicActive = fe ? fe.isFormatActive('em', inspectionRange) : document.queryCommandState('italic');
    var linkInfo = fe ? fe.getActiveLink(inspectionRange) : null;
    var textColor = fe ? fe.getActiveTextColor(inspectionRange) : null;
    var fontSlug = fe && fe.getActiveFontSlug ? fe.getActiveFontSlug(inspectionRange) : null;
    var typographyNode = range.startContainer && range.startContainer.nodeType === 1
      ? range.startContainer
      : range.startContainer && range.startContainer.parentElement;
    var typographyStyle = typographyNode ? window.getComputedStyle(typographyNode) : null;
    var rootTypographyStyle = window.getComputedStyle(document.documentElement);
    _sentSelectionState = 'selected';
    send('text-selected', {
      x: rect.left + rect.width / 2,
      y: rect.top,
      width: rect.width,
      height: rect.height,
      eid: activeEditable.getAttribute('data-pb-eid') || null,
      hasSelection: !range.collapsed,
      bold: boldActive,
      italic: italicActive,
      textColor: textColor,
      fontSlug: fontSlug || '',
      computedFontFamily: typographyStyle ? typographyStyle.fontFamily : '',
      computedFontSize: typographyStyle ? typographyStyle.fontSize : '',
      computedFontWeight: typographyStyle ? typographyStyle.fontWeight : '',
      computedRootFontSize: rootTypographyStyle ? rootTypographyStyle.fontSize : '16px',
      link: linkInfo ? linkInfo.href : null,
      zoom: getCurrentZoom()
    });
    _syncBoundInfoFromSelection();
  }

  function _sendContentChangedNow(sessionId) {
    if (!activeEditable) return null;
    var path = getEditablePath(activeEditable);
    if (!path) return null;
    if (!_activeEditableDirty) return path;
    syncTextEditablePlaceholder(activeEditable);
    _updateEditingPill(activeEditable);
    var currentHtml = activeEditable.innerHTML;
    var removedProtectedPaths = _intentionallyRemovedProtectedPaths.filter(function(path) {
      var currentNode = _resolveNodePath(activeEditable, path);
      var currentProtected = currentNode && currentNode.nodeType === 1
        && currentNode.matches && currentNode.matches(PROTECTED_EDIT_CHILD_SELECTOR);
      return !currentProtected;
    });
    send('content-changed', {
      instanceId: path.instanceId,
      editableIndex: path.editableIndex,
      eid: path.eid || null,
      tagName: path.tagName || '',
      editSessionId: sessionId,
      caret: captureSelectionSnapshot(activeEditable),
      removedProtectedPaths: removedProtectedPaths,
      html: currentHtml
    });
    _intentionallyRemovedProtectedPaths = [];
    _activeEditableDirty = false;
    return path;
  }

  function _restoreSelectionFromLastRange(sel) {
    if (!sel || !lastRange || !activeEditable) return null;
    try {
      if (!activeEditable.contains(lastRange.commonAncestorContainer)) return null;
      sel.removeAllRanges();
      sel.addRange(lastRange);
      return lastRange;
    } catch (e) {
      lastRange = null;
      return null;
    }
  }

  function _getSelectionRangeWithinActiveEditable() {
    if (!activeEditable) return null;
    var sel = window.getSelection();
    if (!sel) return null;
    var range = null;
    if (sel.rangeCount) {
      range = sel.getRangeAt(0);
      try {
        if (range && !activeEditable.contains(range.commonAncestorContainer)) {
          range = null;
        }
      } catch (e) {
        range = null;
      }
    }
    if (!range) {
      range = _restoreSelectionFromLastRange(sel);
    }
    return range || null;
  }

  function _setSelectionToRange(range) {
    var sel = window.getSelection();
    if (!sel || !range) return;
    sel.removeAllRanges();
    sel.addRange(range);
    lastRange = range.cloneRange();
  }

  function _captureEditOperationState() {
    if (!activeEditable) return null;
    return {
      tagName: String(activeEditable.tagName || '').toUpperCase(),
      html: activeEditable.innerHTML,
      selection: captureSelectionSnapshot(activeEditable)
    };
  }

  function _sameEditOperationState(a, b) {
    return !!(a && b && a.tagName === b.tagName && a.html === b.html);
  }

  function _replaceActiveEditableTag(tagName) {
    if (!activeEditable || !activeEditable.parentNode) return activeEditable;
    var desired = String(tagName || '').toUpperCase();
    if (!desired || activeEditable.tagName === desired) return activeEditable;
    var replacement = document.createElement(desired.toLowerCase());
    var attrs = Array.prototype.slice.call(activeEditable.attributes || []);
    for (var i = 0; i < attrs.length; i++) {
      replacement.setAttribute(attrs[i].name, attrs[i].value);
    }
    var wasSelected = _selectedEditable === activeEditable;
    while (activeEditable.firstChild) replacement.appendChild(activeEditable.firstChild);
    activeEditable.parentNode.replaceChild(replacement, activeEditable);
    if (wasSelected) _setSelectedEditable(replacement);
    activeEditable = replacement;
    return replacement;
  }

  function _adoptActiveEditableReplacement(replacement) {
    if (!replacement || replacement.nodeType !== 1 || replacement === activeEditable) return false;
    if (_selectedEditable === activeEditable) _setSelectedEditable(replacement);
    activeEditable = replacement;
    activeEditable.setAttribute('contenteditable', 'true');
    activeEditable.classList.add('pb-editing');
    applyEditingAria(activeEditable);
    _updateEditingPill(activeEditable);
    return true;
  }

  function _restoreEditOperationState(state) {
    if (!activeEditable || !state) return false;
    _replaceActiveEditableTag(state.tagName);
    activeEditable.innerHTML = state.html;
    _focusActiveEditable();
    if (!restoreSelectionSnapshot(activeEditable, state.selection)) {
      var fallbackRange = document.createRange();
      fallbackRange.selectNodeContents(activeEditable);
      fallbackRange.collapse(false);
      _setSelectionToRange(fallbackRange);
    }
    _activeEditableDirty = true;
    onContentChanged();
    return true;
  }

  function _beginEditOperation() {
    return _captureEditOperationState();
  }

  function _commitEditOperation(before) {
    var after = _captureEditOperationState();
    if (!before || !after || _sameEditOperationState(before, after)) return false;
    _editOperationUndoStack.push({ before: before, after: after });
    if (_editOperationUndoStack.length > 50) _editOperationUndoStack.shift();
    _editOperationRedoStack = [];
    return true;
  }

  function _undoEditOperationIfCurrent() {
    if (!activeEditable || !_editOperationUndoStack.length) return false;
    var entry = _editOperationUndoStack[_editOperationUndoStack.length - 1];
    if (!_sameEditOperationState(_captureEditOperationState(), entry.after)) return false;
    _editOperationUndoStack.pop();
    _editOperationRedoStack.push(entry);
    return _restoreEditOperationState(entry.before);
  }

  function _redoEditOperationIfCurrent() {
    if (!activeEditable || !_editOperationRedoStack.length) return false;
    var entry = _editOperationRedoStack[_editOperationRedoStack.length - 1];
    if (!_sameEditOperationState(_captureEditOperationState(), entry.before)) return false;
    _editOperationRedoStack.pop();
    _editOperationUndoStack.push(entry);
    return _restoreEditOperationState(entry.after);
  }

  function _buildSelectionSnapshotPayload(range) {
    if (!activeEditable || !range) return null;
    if (range.collapsed) return null;
    try {
      if (!activeEditable.contains(range.commonAncestorContainer)) return null;
    } catch (e) {
      return null;
    }

    var fragment = range.cloneContents();
    var wrapper = document.createElement('div');
    wrapper.appendChild(fragment);
    var html = wrapper.innerHTML || '';
    var text = range.toString() || '';
    if (!html && !text) return null;

    return {
      eid: activeEditable.getAttribute('data-pb-eid') || null,
      editSessionId: _activeEditSessionId,
      html: html,
      text: text
    };
  }

  function _insertTextAtRange(range, textValue) {
    if (!range) return false;
    range.deleteContents();
    var textNode = document.createTextNode(String(textValue || ''));
    range.insertNode(textNode);
    var nextRange = document.createRange();
    nextRange.setStartAfter(textNode);
    nextRange.collapse(true);
    _setSelectionToRange(nextRange);
    return true;
  }

  function _selectionRectPayloadFromRange(range) {
    var rect = range.getBoundingClientRect();
    return {
      top: rect.top,
      left: rect.left,
      width: rect.width,
      height: rect.height
    };
  }

  function _shouldOpenSlashMenu(range) {
    if (!range || !range.collapsed || !activeEditable) return false;
    var prefix = document.createRange();
    try {
      prefix.selectNodeContents(activeEditable);
      prefix.setEnd(range.startContainer, range.startOffset);
    } catch (e) {
      return false;
    }
    var textBefore = prefix.toString();
    if (!textBefore) return true;
    return /\s$/.test(textBefore);
  }

  function _requestSlashMenu(range) {
    if (!activeEditable || !range) return;
    send('request-slash-menu', {
      rect: _selectionRectPayloadFromRange(range),
      eid: activeEditable.getAttribute('data-pb-eid') || null,
      editSessionId: _activeEditSessionId,
      zoom: getCurrentZoom()
    });
  }

  function _applySlashCommand(command) {
    if (!activeEditable || !command) return false;
    var cmd = String(command).trim().toLowerCase();
    if (!cmd) return false;

    var fe = window.__pbFormatEngine;
    var range = _getSelectionRangeWithinActiveEditable();
    if (!range) return false;

    if (cmd === 'link') {
      send('request-link-popover', {
        eid: activeEditable.getAttribute('data-pb-eid') || null,
        editSessionId: _activeEditSessionId
      });
      return false;
    }

    if (cmd === 'emoji') {
      return _insertTextAtRange(range, '🙂');
    }

    if (cmd === 'phone' || cmd === 'email' || cmd === 'address') {
      var bindingKey = cmd === 'phone'
        ? 'phoneFormatted'
        : (cmd === 'email' ? 'email' : 'addressBlock');
      return _applyBindingFromMessage({
        bindingKey: bindingKey,
        kind: 'span'
      });
    }

    if (!fe) return false;
    if (cmd === 'h1' || cmd === 'h2' || cmd === 'h3') {
      if (typeof fe.convertBlockTag === 'function') {
        var headingResult = fe.convertBlockTag(cmd.toUpperCase(), range, activeEditable);
        _adoptActiveEditableReplacement(headingResult);
        return !!headingResult;
      }
      return false;
    }
    if (cmd === 'bullet') {
      if (typeof fe.toggleBulletList === 'function') {
        var listResult = fe.toggleBulletList(range, activeEditable);
        _adoptActiveEditableReplacement(listResult);
        return !!listResult;
      }
      return false;
    }
    if (cmd === 'divider') {
      if (typeof fe.insertDivider === 'function') {
        var dividerResult = fe.insertDivider(range, activeEditable);
        _adoptActiveEditableReplacement(dividerResult);
        return !!dividerResult;
      }
      return false;
    }
    return false;
  }

  function _applyKeyboardInlineFormat(tagName) {
    if (!activeEditable) return false;
    var fe = window.__pbFormatEngine;
    var range = _getSelectionRangeWithinActiveEditable();
    if (range && range.collapsed && fe && typeof fe.expandRangeToWord === 'function') {
      range = fe.expandRangeToWord(range, activeEditable) || range;
    }
    if (!fe || !range || range.collapsed) {
      send('format-noop', {
        reason: 'no-word-or-selection',
        eid: activeEditable.getAttribute('data-pb-eid') || null,
        editSessionId: _activeEditSessionId
      });
      return false;
    }
    var before = _beginEditOperation();
    fe.toggleFormat(tagName, range);
    _commitEditOperation(before);
    onContentChanged();
    _scheduleSelectionSync();
    return true;
  }

  function _wrapElementPreservingChildren(el) {
    if (!el || !el.parentNode) return;
    var parent = el.parentNode;
    while (el.firstChild) {
      parent.insertBefore(el.firstChild, el);
    }
    parent.removeChild(el);
  }

  function _wrapEditableContentsWithBinding(bindingKey) {
    if (!activeEditable || !bindingKey) return false;
    var span = document.createElement('span');
    span.setAttribute('data-pb-client', bindingKey);
    while (activeEditable.firstChild) {
      span.appendChild(activeEditable.firstChild);
    }
    activeEditable.appendChild(span);
    var sel = window.getSelection();
    if (sel) {
      var nextRange = document.createRange();
      nextRange.selectNodeContents(span);
      nextRange.collapse(false);
      sel.removeAllRanges();
      sel.addRange(nextRange);
      lastRange = nextRange.cloneRange();
    }
    return true;
  }

  function _applySpanBinding(bindingKey, range) {
    if (!bindingKey) return false;
    if (!range || range.collapsed) {
      return _wrapEditableContentsWithBinding(bindingKey);
    }
    var span = document.createElement('span');
    span.setAttribute('data-pb-client', bindingKey);
    try {
      var fragment = range.extractContents();
      span.appendChild(fragment);
      range.insertNode(span);
      var sel = window.getSelection();
      if (sel) {
        var nextRange = document.createRange();
        nextRange.selectNodeContents(span);
        nextRange.collapse(false);
        sel.removeAllRanges();
        sel.addRange(nextRange);
        lastRange = nextRange.cloneRange();
      }
      return true;
    } catch (e) {
      return _wrapEditableContentsWithBinding(bindingKey);
    }
  }

  function _applyAnchorTextBinding(bindingKey, range) {
    if (!bindingKey) return false;
    var anchor = null;
    if (range) {
      anchor = _closestElementWithin(range.startContainer, activeEditable, 'a[href],a[data-pb-client-text]');
      if (!anchor) {
        anchor = _closestElementWithin(range.endContainer, activeEditable, 'a[href],a[data-pb-client-text]');
      }
    }
    if (!anchor && activeEditable.matches && activeEditable.matches('a')) {
      anchor = activeEditable;
    }
    if (!anchor) {
      return _applySpanBinding(bindingKey, range);
    }
    anchor.setAttribute('data-pb-client-text', bindingKey);
    if (range) {
      var sel = window.getSelection();
      if (sel) {
        sel.removeAllRanges();
        sel.addRange(range);
      }
      lastRange = range.cloneRange();
    }
    return true;
  }

  function _applyBindingFromMessage(data) {
    if (!activeEditable || !data) return false;
    var bindingKey = String(data.bindingKey || '').trim();
    if (!bindingKey) return false;
    var kind = String(data.kind || 'span').trim().toLowerCase();
    var range = _getSelectionRangeWithinActiveEditable();
    if (kind === 'anchor-text') {
      return _applyAnchorTextBinding(bindingKey, range);
    }
    return _applySpanBinding(bindingKey, range);
  }

  function _removeBindingFromActiveEditable(data) {
    if (!activeEditable) return false;
    var targetKey = String(data && data.bindingKey ? data.bindingKey : '').trim();
    var removed = false;
    var range = _getSelectionRangeWithinActiveEditable();

    var anchor = range ? _closestElementWithin(range.startContainer, activeEditable, 'a[data-pb-client-text]') : null;
    if (!anchor && activeEditable.matches && activeEditable.matches('a[data-pb-client-text]')) {
      anchor = activeEditable;
    }
    if (anchor) {
      var anchorKey = anchor.getAttribute('data-pb-client-text') || '';
      if (!targetKey || targetKey === anchorKey) {
        anchor.removeAttribute('data-pb-client-text');
        removed = true;
      }
    }

    var span = range ? _closestElementWithin(range.startContainer, activeEditable, '[data-pb-client]') : null;
    if (!span && activeEditable.hasAttribute && activeEditable.hasAttribute('data-pb-client')) {
      span = activeEditable;
    }
    if (span) {
      var spanKey = span.getAttribute('data-pb-client') || '';
      if (!targetKey || targetKey === spanKey) {
        if (span === activeEditable) span.removeAttribute('data-pb-client');
        else _wrapElementPreservingChildren(span);
        removed = true;
      }
    }

    if (!removed) {
      if (targetKey) {
        var keyedAnchor = activeEditable.querySelector('a[data-pb-client-text="' + _escapeSelectorValue(targetKey) + '"]');
        if (keyedAnchor) {
          keyedAnchor.removeAttribute('data-pb-client-text');
          removed = true;
        }
        var keyedSpan = activeEditable.querySelector('[data-pb-client="' + _escapeSelectorValue(targetKey) + '"]');
        if (keyedSpan) {
          _wrapElementPreservingChildren(keyedSpan);
          removed = true;
        }
      } else {
        var anyAnchor = activeEditable.querySelector('a[data-pb-client-text]');
        if (anyAnchor) {
          anyAnchor.removeAttribute('data-pb-client-text');
          removed = true;
        }
        var anySpan = activeEditable.querySelector('[data-pb-client]');
        if (anySpan) {
          _wrapElementPreservingChildren(anySpan);
          removed = true;
        }
      }
    }

    return removed;
  }

  function onContentChanged() {
    _activeEditableDirty = true;
    _updateEditingPill(activeEditable);
    _scheduleSelectionSync();
    clearTimeout(debounceTimer);
    if (_isComposingText) {
      debounceTimer = null;
      return;
    }
    var scheduledSessionId = _activeEditSessionId;
    debounceTimer = setTimeout(function() {
      if (!activeEditable) return;
      if (scheduledSessionId !== _activeEditSessionId) return;
      _sendContentChangedNow(scheduledSessionId);
    }, 300);
  }

  function insertLineBreakAtSelection(editable) {
    if (!editable) return false;
    var sel = window.getSelection();
    if (!sel || !sel.rangeCount) return false;

    var range = sel.getRangeAt(0);
    try {
      if (!editable.contains(range.commonAncestorContainer)) return false;
    } catch (e) {
      return false;
    }

    range.deleteContents();

    var br = document.createElement('br');
    range.insertNode(br);

    // Keep caret after the inserted <br> so repeated Shift+Enter works naturally.
    var spacer = document.createTextNode('');
    if (br.parentNode) {
      br.parentNode.insertBefore(spacer, br.nextSibling);
      range.setStart(spacer, 0);
      range.collapse(true);
    } else {
      range.setStartAfter(br);
      range.collapse(true);
    }

    sel.removeAllRanges();
    sel.addRange(range);
    lastRange = range.cloneRange();
    return true;
  }

  function _applyNativeHistoryShortcut(e, key) {
    if (!activeEditable || e.altKey || e.isComposing) return false;

    var command = null;
    if (key === 'z') {
      command = e.shiftKey ? 'redo' : 'undo';
    } else if (key === 'y' && !e.shiftKey) {
      command = 'redo';
    }
    if (!command) return false;

    e.preventDefault();
    if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
    else e.stopPropagation();

    var beforeHtml = activeEditable.innerHTML;
    _focusActiveEditable();

    var historyRange = _getSelectionRangeWithinActiveEditable();
    if (historyRange) _setSelectionToRange(historyRange);

    if (command === 'undo' && _undoEditOperationIfCurrent()) {
      return true;
    }
    if (command === 'redo' && _redoEditOperationIfCurrent()) {
      return true;
    }
    if (command === 'undo' && _undoLastPasteIfCurrent()) return true;
    if (command === 'redo' && _redoLastUndonePaste()) return true;

    try {
      document.execCommand(command, false, null);
    } catch (historyErr) {}

    requestAnimationFrame(function() {
      if (!activeEditable) return;
      if (activeEditable.innerHTML !== beforeHtml) {
        onContentChanged();
      } else {
        _scheduleSelectionSync();
      }
    });
    return true;
  }

  function finishEditing() {
    if (!activeEditable) return;
    clearTimeout(debounceTimer);
    debounceTimer = null;
    var closingSessionId = _activeEditSessionId;
    _activeEditSessionId = _nextEditSessionId();
    var caretSnapshot = captureSelectionSnapshot(activeEditable);
    var path = getEditablePath(activeEditable);
    _sendContentChangedNow(closingSessionId);
    activeEditable.removeAttribute('contenteditable');
    activeEditable.classList.remove('pb-editing');
    _restoreSuspendedDragTargets();
    _clearEditingPill(activeEditable);
    clearEditingAria(activeEditable);
    syncTextEditablePlaceholder(activeEditable);
    _sendSelectionCleared();
    if (path) {
      send('editing-complete', {
        instanceId: path.instanceId,
        editableIndex: path.editableIndex,
        eid: path.eid || null,
        editableType: path.editableType,
        tagName: path.tagName || '',
        editSessionId: closingSessionId,
        caret: caretSnapshot
      });
    } else {
      send('editing-complete', {
        editSessionId: closingSessionId,
        caret: caretSnapshot
      });
    }
    activeEditable = null;
    _plainTextOnly = false;
    activeEditableInitialHTML = null;
    activeEditableInitialTagName = null;
    lastRange = null;
    _lastPastePayload = null;
    _lastPasteRangeSnapshot = null;
    _lastPasteInsertedNodes = null;
    _lastPasteReplacedNodes = null;
    _lastUndonePastePayload = null;
    _editOperationUndoStack = [];
    _editOperationRedoStack = [];
    _isComposingText = false;
    _escapeCancelArmedUntil = 0;
    _intentionallyRemovedProtectedPaths = [];
    _activeEditableDirty = false;
  }

  function cancelEditing() {
    if (!activeEditable) return;
    clearTimeout(debounceTimer);
    debounceTimer = null;
    var closingSessionId = _activeEditSessionId;
    _activeEditSessionId = _nextEditSessionId();
    var caretSnapshot = captureSelectionSnapshot(activeEditable);
    var path = getEditablePath(activeEditable);
    if (activeEditableInitialTagName) {
      _replaceActiveEditableTag(activeEditableInitialTagName);
      path = getEditablePath(activeEditable) || path;
    }
    if (activeEditableInitialHTML != null) {
      activeEditable.innerHTML = activeEditableInitialHTML;
    }
    activeEditable.removeAttribute('contenteditable');
    activeEditable.classList.remove('pb-editing');
    _restoreSuspendedDragTargets();
    _clearEditingPill(activeEditable);
    clearEditingAria(activeEditable);
    syncTextEditablePlaceholder(activeEditable);
    _sendSelectionCleared();
    if (path) {
      send('editing-cancelled', {
        instanceId: path.instanceId,
        editableIndex: path.editableIndex,
        eid: path.eid || null,
        editableType: path.editableType,
        tagName: path.tagName || '',
        editSessionId: closingSessionId,
        caret: caretSnapshot
      });
    } else {
      send('editing-cancelled', {
        editSessionId: closingSessionId,
        caret: caretSnapshot
      });
    }
    activeEditable = null;
    _plainTextOnly = false;
    activeEditableInitialHTML = null;
    activeEditableInitialTagName = null;
    lastRange = null;
    _lastPastePayload = null;
    _lastPasteRangeSnapshot = null;
    _lastPasteInsertedNodes = null;
    _lastPasteReplacedNodes = null;
    _lastUndonePastePayload = null;
    _editOperationUndoStack = [];
    _editOperationRedoStack = [];
    _isComposingText = false;
    _escapeCancelArmedUntil = 0;
    _intentionallyRemovedProtectedPaths = [];
    _activeEditableDirty = false;
  }

  // --- Selection tracking for text-format controls ---
  document.addEventListener('selectionchange', function() {
    if (!activeEditable) return;
    _scheduleSelectionSync();
  });

  window.addEventListener('scroll', function() {
    _scheduleSelectionSync();
  }, { passive: true });

  window.addEventListener('resize', function() {
    _scheduleSelectionSync();
  }, { passive: true });

  // --- Keyboard handler ---
  // Capture text-edit undo/redo before the browser can route Cmd/Ctrl+Z to
  // tab/session-level native undo. This is especially important after paste,
  // where the contenteditable history stack can be fragile in Safari.
  document.addEventListener('keydown', function(e) {
    var key = (e.key || '').toLowerCase();
    var isMod = !!(e.metaKey || e.ctrlKey);
    if (activeEditable && isMod && (key === 'z' || key === 'y')) {
      _applyNativeHistoryShortcut(e, key);
    }
  }, true);

  document.addEventListener('keydown', function(e) {
    var key = (e.key || '').toLowerCase();
    var isMod = !!(e.metaKey || e.ctrlKey);

    if (_reorderState && _reorderState.activated && e.key === 'Escape') {
      e.preventDefault();
      _cancelReorder('escape');
      return;
    }

    if (e.key === 'Escape' && activeEditable) {
      e.preventDefault();
      if (_activeEditableDirty && Date.now() > _escapeCancelArmedUntil) {
        _escapeCancelArmedUntil = Date.now() + 3000;
        send('edit-cancel-confirmation-requested', {
          eid: activeEditable.getAttribute('data-pb-eid') || null,
          editSessionId: _activeEditSessionId
        });
        return;
      }
      cancelEditing();
      return;
    }

    // Selected text is a stable keyboard state: Enter promotes it into edit
    // mode without requiring a pointer double-click.
    if (!activeEditable && e.key === 'Enter' && !isMod && !e.altKey && !e.shiftKey && !e.isComposing &&
        _selectedEditable && _selectedEditable.getAttribute('data-pb-editable') === 'text') {
      var selectedTextPath = getEditablePath(_selectedEditable);
      if (selectedTextPath) {
        e.preventDefault();
        e.stopPropagation();
        selectedTextPath.editIntent = true;
        send('text-edit-requested', selectedTextPath);
        return;
      }
    }

    // While editing text, only Cmd/Ctrl+S should commit and route to parent.
    // Keep Cmd/Ctrl+Z and redo native inside contenteditable so users get
    // word-level undo/redo instead of ending the entire edit session.
    if (activeEditable && isMod && (key === 'z' || key === 'y') && _applyNativeHistoryShortcut(e, key)) {
      return;
    }

    if (activeEditable && isMod && key === 's') {
      e.preventDefault();
      e.stopPropagation();
      finishEditing();
      send('shortcut-keydown', {
        key: e.key || '',
        code: e.code || '',
        ctrlKey: !!e.ctrlKey,
        metaKey: !!e.metaKey,
        shiftKey: !!e.shiftKey,
        altKey: !!e.altKey,
        isComposing: !!e.isComposing
      });
      return;
    }

    if (activeEditable && _plainTextOnly && isMod && !e.altKey && (key === 'b' || key === 'i' || key === 'k')) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    if (activeEditable && isMod && !e.altKey && (key === 'b' || key === 'i')) {
      e.preventDefault();
      e.stopPropagation();
      _applyKeyboardInlineFormat(key === 'b' ? 'strong' : 'em');
      return;
    }

    // Cmd/Ctrl+K is link editing while text is active. Outside an edit the
    // parent keeps the same shortcut for the global command palette.
    if (activeEditable && isMod && key === 'k' && !e.altKey) {
      e.preventDefault();
      e.stopPropagation();
      send('request-link-popover', {
        eid: activeEditable.getAttribute('data-pb-eid') || null,
        editSessionId: _activeEditSessionId
      });
      return;
    }

    // Find/replace is a parent surface, so close the edit cleanly before
    // forwarding. Message ordering guarantees editing-complete lands first.
    if (activeEditable && isMod && key === 'f' && !e.altKey) {
      e.preventDefault();
      e.stopPropagation();
      finishEditing();
      send('shortcut-keydown', {
        key: e.key || '', code: e.code || '', ctrlKey: !!e.ctrlKey,
        metaKey: !!e.metaKey, shiftKey: !!e.shiftKey, altKey: !!e.altKey,
        isComposing: !!e.isComposing
      });
      return;
    }

    // Let the browser perform its normal focus traversal, but make Tab an
    // explicit edit boundary so the transaction cannot remain stranded.
    if (activeEditable && e.key === 'Tab' && !isMod && !e.altKey && !e.isComposing) {
      finishEditing();
      return;
    }

    // ClipboardEvent does not expose keyboard modifiers consistently. Remember
    // the shortcut briefly so the paste handler can select the plain-text profile.
    if (activeEditable && isMod && key === 'v' && e.shiftKey && !e.altKey) {
      _plainTextPasteRequestedUntil = Date.now() + 1000;
    }

    // In text editing, force Shift+Enter to insert a literal <br>.
    if (activeEditable && _plainTextOnly && e.key === 'Enter' && !isMod && !e.altKey && !e.isComposing) {
      e.preventDefault();
      e.stopPropagation();
      finishEditing();
      return;
    }

    if (activeEditable && e.key === 'Enter' && e.shiftKey && !isMod && !e.altKey && !e.isComposing) {
      e.preventDefault();
      e.stopPropagation();
      if (insertLineBreakAtSelection(activeEditable)) {
        onContentChanged();
      }
      return;
    }

    // Plain Enter inside a text-editable inserts a <br> line break rather than
    // letting the browser inject a block <div> (which the sanitizer would unwrap,
    // corrupting/vanishing the line). Skip when focus is in an embedded real form
    // control (INPUT/TEXTAREA/SELECT) so their native Enter behavior is preserved —
    // note isTypingTarget() also matches contenteditable, so we test tag names here.
    if (activeEditable && e.key === 'Enter' && !e.shiftKey && !isMod && !e.altKey && !e.isComposing) {
      var _enterTag = e.target && e.target.tagName;
      var _enterInFormField = _enterTag === 'INPUT' || _enterTag === 'TEXTAREA' || _enterTag === 'SELECT';
      if (!_enterInFormField) {
        e.preventDefault();
        e.stopPropagation();
        if (insertLineBreakAtSelection(activeEditable)) {
          onContentChanged();
        }
        return;
      }
    }

    if (activeEditable && !_plainTextOnly && e.key === '/' && !isMod && !e.altKey && !e.isComposing) {
      var slashRange = _getSelectionRangeWithinActiveEditable();
      if (slashRange && _shouldOpenSlashMenu(slashRange)) {
        e.preventDefault();
        e.stopPropagation();
        _requestSlashMenu(slashRange);
        return;
      }
    }

    // Forward top-level builder shortcuts to parent so they work while
    // the iframe has focus. Keep native typing behavior in form fields.
    if (isTypingTarget(e.target)) return;

    var isBuilderShortcut = false;

    if (isMod) {
      isBuilderShortcut = (
        key === 'z' || key === 'y' || key === 's' || key === 'd' || key === 'k' ||
        key === 'c' || key === 'x' || key === 'v' ||
        (e.shiftKey && (key === 'arrowup' || key === 'arrowdown')) ||
        key === '=' || key === '+' || key === 'add' || key === '-' || key === 'subtract' || key === '0'
      );
    } else if (!activeEditable) {
      // Plain "/" opens the command palette via the parent shortcut handler.
      // Shifted "/" produces "?" on US layouts (caught by the "?" check);
      // non-US layouts that still report "/" with shift are covered too.
      isBuilderShortcut = (
        key === 'escape' || key === 'delete' || key === 'backspace' ||
        key === '?' || key === '/'
      );
    }

    if (!isBuilderShortcut) return;

    e.preventDefault();
    e.stopPropagation();
    send('shortcut-keydown', {
      key: e.key || '',
      code: e.code || '',
      ctrlKey: !!e.ctrlKey,
      metaKey: !!e.metaKey,
      shiftKey: !!e.shiftKey,
      altKey: !!e.altKey,
      isComposing: !!e.isComposing
    });
  });

  // --- Icon tinting ---
  var _TRANSPARENT_SVG = 'data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22/%3E';

  function _isSvgUrl(src) {
    try { return new URL(src, 'https://x').pathname.toLowerCase().endsWith('.svg'); }
    catch(e) { return false; }
  }

  function _canMaskSvgSource(src) {
    if (!src) return false;
    var raw = String(src).trim();
    if (!raw) return false;
    if (raw.indexOf('data:') === 0 || raw.indexOf('blob:') === 0) return true;
    var docOrigin = (window.location && window.location.origin) ? window.location.origin : '';
    if (!docOrigin || docOrigin === 'null') return false;
    try {
      var resolved = new URL(raw, document.baseURI || window.location.href);
      if (resolved.protocol !== 'http:' && resolved.protocol !== 'https:') return true;
      return resolved.origin === docOrigin;
    } catch (e) {
      return false;
    }
  }

  function _applyIconTints() {
    var icons = document.querySelectorAll('img.cs-icon:not([data-tinted])');
    for (var i = 0; i < icons.length; i++) {
      var icon = icons[i];
      var src = icon.getAttribute('src');
      if (!src || !_isSvgUrl(src) || !_canMaskSvgSource(src)) continue;
      icon.setAttribute('data-orig-src', src);
      icon.setAttribute('data-tinted', '');
      var existing = icon.getAttribute('style') || '';
      var maskCSS = '-webkit-mask-image:url("' + src + '");mask-image:url("' + src + '");-webkit-mask-size:contain;mask-size:contain;-webkit-mask-repeat:no-repeat;mask-repeat:no-repeat;-webkit-mask-position:center;mask-position:center;background-color:var(--pb-icon-tint, var(--primary))';
      var tintOverride = icon.getAttribute('data-tint-color');
      if (tintOverride) {
        maskCSS += ';--pb-icon-tint:' + (tintOverride.charAt(0) === '#' ? tintOverride : 'var(' + tintOverride + ')');
      }
      icon.setAttribute('style', existing ? existing.replace(/;?\s*$/, ';') + maskCSS : maskCSS);
      icon.setAttribute('src', _TRANSPARENT_SVG);
    }
  }

  function _removeIconTints() {
    var icons = document.querySelectorAll('img.cs-icon[data-tinted]');
    for (var i = 0; i < icons.length; i++) {
      var icon = icons[i];
      var orig = icon.getAttribute('data-orig-src');
      if (orig) icon.setAttribute('src', orig);
      icon.removeAttribute('data-tinted');
      icon.removeAttribute('data-orig-src');
      var style = icon.getAttribute('style') || '';
      var cleaned = style.split(';').filter(function(s) {
        var prop = (s.split(':')[0] || '').trim().toLowerCase();
        return prop && prop.indexOf('mask-image') === -1 && prop.indexOf('mask-size') === -1 && prop.indexOf('mask-repeat') === -1 && prop.indexOf('mask-position') === -1 && prop !== 'background-color' && prop !== '--pb-icon-tint';
      }).join(';').replace(/^;+|;+$/g, '');
      if (cleaned) icon.setAttribute('style', cleaned);
      else icon.removeAttribute('style');
    }
  }
`;
}
